"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

const NUMBERED = "numbered";
const INDENT_RE = /^[\t ]/;
const SUB_INDENT = "    ";

type Strings = { [K in keyof typeof BULLET_POINT_GENERATOR_STRINGS]: string };

const BULLET_STYLES: { value: string; label: keyof Strings }[] = [
  { value: "•", label: "styleDot" },
  { value: "◦", label: "styleHollow" },
  { value: "▪", label: "styleSquare" },
  { value: "–", label: "styleDash" },
  { value: "→", label: "styleArrow" },
  { value: "✓", label: "styleCheck" },
  { value: "✗", label: "styleCross" },
  { value: NUMBERED, label: "styleNumbered" },
];

function buildNumberedList(lines: string[]): string {
  const out: string[] = [];
  let topCount = 0;
  let subCount = 0;
  for (const line of lines) {
    if (INDENT_RE.test(line)) {
      subCount += 1;
      out.push(`${SUB_INDENT}${subCount}. ${line.trim()}`);
    } else {
      topCount += 1;
      subCount = 0;
      out.push(`${topCount}. ${line.trim()}`);
    }
  }
  return out.join("\n");
}

function buildBulletList(text: string, bullet: string): string {
  const lines = text.split("\n").filter((l) => l.trim().length > 0);
  if (bullet === NUMBERED) {
    return buildNumberedList(lines);
  }
  return lines
    .map((line) => {
      if (INDENT_RE.test(line)) {
        return `${SUB_INDENT}${bullet} ${line.trim()}`;
      }
      return `${bullet} ${line.trim()}`;
    })
    .join("\n");
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const BULLET_POINT_GENERATOR_STRINGS = {
  bulletStyle: "Bullet style",
  inputOneItemPerLine:
    "Input — one item per line (start a line with a space or tab for a sub-item)",
  // The indent on line 2 is the placeholder's point — it demonstrates a sub-point.
  firstPointSubPointSecond:
    "First point\n Sub-point\nSecond point\nThird point",
  item: "one: item|other: items",
  generateBullets: "Generate Bullets",
  copied: "Copied!",
  copyList: "Copy List",
  clear: "Clear",
  bulletedList: "Bulleted list",
  theseArePlainTextBullets:
    "These are plain-text bullets — they paste anywhere: documents, emails, chat apps, and social media.",
  listItems: "List items",
  characters: "Characters",
  styleDot: "• Dot",
  styleHollow: "◦ Hollow",
  styleSquare: "▪ Square",
  styleDash: "– Dash",
  styleArrow: "→ Arrow",
  styleCheck: "✓ Check",
  styleCross: "✗ Cross",
  styleNumbered: "1. Numbered",
} as const;

export default function BulletPointGeneratorWidget() {
  const [input, setInput] = useState("");
  const [bullet, setBullet] = useState("•");
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const itemCount = input.split("\n").filter((l) => l.trim().length > 0).length;
  const outputItemCount =
    output.trim().length === 0 ? 0 : output.split("\n").length;

  function handleGenerate() {
    if (input.trim().length === 0) {
      return;
    }
    setOutput(buildBulletList(input, bullet));
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setCopied(false);
  }

  const locale = useToolLocale();
  const strings = useToolUi(BULLET_POINT_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const selectedStyle = BULLET_STYLES.find((b) => b.value === bullet);
  const styleLabel = selectedStyle ? strings[selectedStyle.label] : bullet;

  return (
    <>
      {/* Bullet style */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.bulletStyle}
        </p>
        <div className="flex flex-wrap gap-3">
          {BULLET_STYLES.map((b) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={b.value}
            >
              <input
                checked={bullet === b.value}
                className="accent-primary"
                name="bullet-style"
                onChange={() => setBullet(b.value)}
                type="radio"
                value={b.value}
              />
              {strings[b.label]}
            </label>
          ))}
        </div>
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="bullet-input"
        >
          {strings.inputOneItemPerLine}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="bullet-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.firstPointSubPointSecond}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {fmt(itemCount)}
          {tPlural(strings.item, locale, itemCount)}
        </p>
      </div>
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateBullets}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyList}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="bullet-output"
          >
            {strings.bulletedList}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="bullet-output"
            readOnly
            value={output}
          />
          <p className="text-muted-foreground text-xs">
            {strings.theseArePlainTextBullets}
          </p>
        </div>
      )}
      {/* Stats */}
      {output.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard label={strings.listItems} value={fmt(outputItemCount)} />
          <StatCard label={strings.bulletStyle} value={styleLabel} />
          <StatCard label={strings.characters} value={fmt(output.length)} />
        </div>
      )}
    </>
  );
}
